import { createContext, useContext, useEffect, useRef } from 'react';
import { cue } from './feedback';
import { useServerClock } from './hooks';

/**
 * The time left on a paused clock, published by the room screen so every timer
 * inside it holds still without each one being told separately. Null when the
 * clock is running.
 */
export const PausedClock = createContext<number | null>(null);

/**
 * A bright bar that drains as a deadline approaches, and pulses once it is nearly
 * out.
 *
 * Driven by an absolute server deadline rather than a local countdown, so every
 * phone in the room agrees on how long is left however laggy the connection.
 */
export function TimerBar({
  endsAt,
  totalMs,
  serverNow,
  pausedRemainingMs = null,
  label,
}: {
  endsAt: number | null;
  /** The full duration, so the bar knows what "full" looks like. */
  totalMs: number;
  serverNow(): number;
  /** Set while a host has the clock frozen; the bar holds its position. */
  pausedRemainingMs?: number | null;
  /** Announced to screen readers alongside the seconds remaining. */
  label?: string;
}) {
  const roomPause = useContext(PausedClock);
  const frozen = pausedRemainingMs ?? (endsAt === null ? roomPause : null);
  const now = useServerClock(serverNow, endsAt !== null && frozen === null);

  const remainingMs = frozen !== null ? frozen : endsAt === null ? totalMs : Math.max(0, endsAt - now);

  const fraction = totalMs > 0 ? Math.max(0, Math.min(1, remainingMs / totalMs)) : 0;
  const seconds = Math.ceil(remainingMs / 1000);

  const urgent = fraction <= 0.25;
  const colour = frozen !== null ? 'var(--muted)' : fraction > 0.5 ? 'var(--cyan)' : urgent ? 'var(--red)' : 'var(--amber)';

  // A light tick for each of the last five seconds, felt rather than watched.
  const lastTick = useRef<number | null>(null);
  useEffect(() => {
    if (frozen !== null || endsAt === null) return;
    if (seconds > 0 && seconds <= 5 && lastTick.current !== seconds) cue('tick');
    lastTick.current = seconds;
  }, [seconds, frozen, endsAt]);

  return (
    <div className="col" style={{ gap: 4 }}>
      <div
        role="timer"
        aria-label={`${label ? label + ': ' : ''}${seconds} seconds left${frozen !== null ? ', paused' : ''}`}
        style={{
          height: 12,
          borderRadius: 999,
          background: 'rgba(255,255,255,0.07)',
          overflow: 'hidden',
        }}
      >
        <div
          className={urgent && frozen === null ? 'flicker' : undefined}
          style={{
            height: '100%',
            width: `${fraction * 100}%`,
            background: colour,
            boxShadow: `0 0 12px ${colour}`,
            transition: 'width 0.1s linear, background 0.4s linear',
          }}
        />
      </div>
      <div className="sign center" style={{ fontSize: 13, color: colour }}>
        {frozen !== null ? `⏸ ${seconds}` : seconds}
      </div>
    </div>
  );
}
